import React, { useMemo, useEffect, useContext } from 'react';
import './Components.css';
import { TaskIdDataContext } from '../contexts/AppContext';
import { GetSetupHeader } from '../isdlData/sessionData';
import { GetSetupLocation } from '../isdlData/sessionData';

const REQUIRED_TOTAL_SHOTS = 22;
const PLATE_SHOT_COUNT = 12;
const HEAD_SHOT_COUNT = 10;
const COR_PLATE_TYPE = 'Plate';

function parseNumber(value) {
    if (value === null || value === undefined) {
        return null;
    }

    const num = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(num) ? num : null;
}

function isValidFaceDeformationInput(value) {
    const normalized = (value ?? '').toString().trim();

    if (normalized === '') {
        return true;
    }

    return /^-?(?:\d+)?(?:\.\d{0,4})?$/.test(normalized);
}

function getMeasurement(lmInfo, key) {
    if (!lmInfo) {
        return null;
    }
    if (lmInfo.Measurements && lmInfo.Measurements[key] !== undefined) {
        return lmInfo.Measurements[key];
    }
    if (lmInfo.CalculatedMeasurements && lmInfo.CalculatedMeasurements[key] !== undefined) {
        return lmInfo.CalculatedMeasurements[key];
    }
    return null;
}

function toRadians(deg) {
    return (deg * Math.PI) / 180;
}

function getResearchQuadShots(rows = []) {
    if (!rows || rows.length === 0) {
        return [];
    }

    let index = 0;
    const shots = [];

    rows.forEach((setup) => {
        if (!setup || !Array.isArray(setup.Shots)) {
            return;
        }

        setup.Shots.forEach((shot) => {
            if (!shot || !Array.isArray(shot.LMInfos)) {
                return;
            }

            const lmInfo = shot.LMInfos.find((info) => info?.LMType === 'RESEARCH_QUAD');
            if (!lmInfo) {
                return;
            }

            shots.push({ shot, lmInfo, index });
            index += 1;
        });
    });

    const timeFromShot = (shot) => {
        const raw = shot?.TimeStamp || shot?.TimeStampLocal;
        const parsed = raw ? Date.parse(raw) : NaN;
        return Number.isFinite(parsed) ? parsed : null;
    };

    return shots.sort((a, b) => {
        const aNum = a.shot?.ShotNumber;
        const bNum = b.shot?.ShotNumber;

        if (typeof aNum === 'number' && typeof bNum === 'number') {
            return aNum - bNum;
        }
        if (typeof aNum === 'number') {
            return -1;
        }
        if (typeof bNum === 'number') {
            return 1;
        }

        const aTime = timeFromShot(a.shot);
        const bTime = timeFromShot(b.shot);

        if (aTime !== null && bTime !== null) {
            return aTime - bTime;
        }
        if (aTime !== null) {
            return -1;
        }
        if (bTime !== null) {
            return 1;
        }

        return a.index - b.index;
    });
}

function isPlateCorSetup(setup) {
    return setup?.TestParameters?.CORType === COR_PLATE_TYPE;
}

function computeGroupCor(shots, ballMass, targetMass) {
    const eUsgaValues = [];
    let invalidShots = 0;

    if (!Number.isFinite(ballMass) || !Number.isFinite(targetMass) || targetMass <= 0) {
        return { eUsgaValues, invalidShots: shots.length };
    }

    const massRatio = ballMass / targetMass;

    shots.forEach(({ lmInfo }) => {
        const preSpeed = parseNumber(getMeasurement(lmInfo, 'pre_BallSpeedMph'));
        const preLaunch = parseNumber(getMeasurement(lmInfo, 'pre_BallLaunchAngleDeg'));
        const preAzimuth = parseNumber(getMeasurement(lmInfo, 'pre_BallSideAngleDeg'));

        const postSpeed = parseNumber(getMeasurement(lmInfo, 'post_BallSpeedMph'));
        const postLaunch = parseNumber(getMeasurement(lmInfo, 'post_BallLaunchAngleDeg'));
        const postAzimuth = parseNumber(getMeasurement(lmInfo, 'post_BallSideAngleDeg'));

        if (
            preSpeed === null ||
            preLaunch === null ||
            preAzimuth === null ||
            postSpeed === null ||
            postLaunch === null ||
            postAzimuth === null ||
            preSpeed === 0
        ) {
            invalidShots += 1;
            return;
        }

        const v1Adj = preSpeed * Math.cos(toRadians(preAzimuth)) * Math.cos(toRadians(preLaunch));
        const v2Adj = postSpeed * Math.cos(toRadians(postAzimuth)) * Math.cos(toRadians(postLaunch));

        if (!Number.isFinite(v1Adj) || !Number.isFinite(v2Adj) || v1Adj === 0) {
            invalidShots += 1;
            return;
        }

        const usgaRatio = v2Adj / v1Adj;
        const eUsga = usgaRatio * (1 + massRatio) + massRatio;

        if (Number.isFinite(eUsga)) {
            eUsgaValues.push(eUsga);
        } else {
            invalidShots += 1;
        }
    });

    if (eUsgaValues.length < 3) {
        return { eUsgaValues, invalidShots };
    }

    const sum = eUsgaValues.reduce((acc, val) => acc + val, 0);
    const avg = sum / eUsgaValues.length;
    const max = Math.max(...eUsgaValues);
    const min = Math.min(...eUsgaValues);
    const trimmed = (eUsgaValues.length * avg - max - min) / (eUsgaValues.length - 2);

    return {
        eUsgaValues,
        invalidShots,
        trimmed
    };
}

export function buildResearchQuadCorPayload({ masses, rows = [], selectedSetup = null, plateSetup = null }) {
    const resolvedPlateSetup = plateSetup || (isPlateCorSetup(selectedSetup) ? selectedSetup : null);
    const resolvedHeadSetup = selectedSetup && !isPlateCorSetup(selectedSetup) ? selectedSetup : null;
    const fallbackRows = Array.isArray(rows) ? rows : [];

    const plateResearchQuadShots = resolvedPlateSetup ? getResearchQuadShots([resolvedPlateSetup]) : [];
    const headResearchQuadShots = resolvedHeadSetup
        ? getResearchQuadShots([resolvedHeadSetup])
        : (!resolvedPlateSetup ? getResearchQuadShots(fallbackRows) : []);

    const checkedPlateResearchQuadShots = plateResearchQuadShots.filter(({ shot }) => shot?.IsChecked);
    const checkedHeadResearchQuadShots = headResearchQuadShots.filter(({ shot }) => shot?.IsChecked);

    const plateMass = parseNumber(masses?.plate);
    const headMass = parseNumber(masses?.head);
    
    let plateBallMass = parseNumber(masses?.plateBall);
    let headBallMass = parseNumber(masses?.headBall);
    
    const plateShots = checkedPlateResearchQuadShots.slice(0, PLATE_SHOT_COUNT);
    const headShots = checkedHeadResearchQuadShots.slice(0, HEAD_SHOT_COUNT);

    const plateResult = computeGroupCor(plateShots, plateBallMass, plateMass);
    const headResult = computeGroupCor(headShots, headBallMass, headMass);

    const plateCor = plateResult?.trimmed ?? null;
    const headCor = headResult?.trimmed ?? null;
    const plateAdjustment = plateCor !== null ? 0.822 - plateCor : null;
    const adjustedHeadCor = headCor !== null && plateAdjustment !== null ? headCor + plateAdjustment : null;
    const differential = headCor !== null && plateCor !== null ? headCor - plateCor : null;
    const rawFaceDeformationStart = (
        masses?.faceDeformationStart ??
        selectedSetup?.CannonCor?.results?.faceDeformationStart ??
        ''
    );
    const rawFaceDeformationEnd = (
        masses?.faceDeformationEnd ??
        selectedSetup?.CannonCor?.results?.faceDeformationEnd ??
        ''
    );
    const parsedFaceDeformationStart = parseNumber(rawFaceDeformationStart);
    const parsedFaceDeformationEnd = parseNumber(rawFaceDeformationEnd);
    const faceDeformationDelta = (
        parsedFaceDeformationStart !== null &&
        parsedFaceDeformationEnd !== null
    )
        ? parsedFaceDeformationEnd - parsedFaceDeformationStart
        : null;
    const shotsOk = plateShots.length + headShots.length === REQUIRED_TOTAL_SHOTS;
    const plateCountOk = plateShots.length === PLATE_SHOT_COUNT;
    const headCountOk = headShots.length === HEAD_SHOT_COUNT;
    const massesOk =
        Number.isFinite(plateMass) &&
        Number.isFinite(headMass) &&
        Number.isFinite(plateBallMass) &&
        Number.isFinite(headBallMass);
    const corReady = shotsOk && massesOk && plateCountOk && headCountOk && plateCor !== null && headCor !== null && adjustedHeadCor !== null;
    const plateCaution = plateCor !== null ? Math.abs(0.84 - plateCor) > 0.004 : false;
    const confirmationSignature = JSON.stringify({
        inputs: {
            plateMass,
            headMass,
            plateBallMass,
            headBallMass
        },
        plateSetupId: resolvedPlateSetup?.Id ?? null,
        headSetupId: resolvedHeadSetup?.Id ?? null,
        plateShots: plateShots.map(({ shot, index }) => ({
            index,
            shotNumber: shot?.ShotNumber ?? null,
            isChecked: !!shot?.IsChecked,
            timeStamp: shot?.TimeStamp ?? null,
            timeStampLocal: shot?.TimeStampLocal ?? null
        })),
        headShots: headShots.map(({ shot, index }) => ({
            index,
            shotNumber: shot?.ShotNumber ?? null,
            isChecked: !!shot?.IsChecked,
            timeStamp: shot?.TimeStamp ?? null,
            timeStampLocal: shot?.TimeStampLocal ?? null
        }))
    });

    return {
        inputs: {
            plateMass,
            headMass,
            plateBallMass,
            headBallMass
        },
        results: {
            plateRawCor: Number.isFinite(plateCor) ? Number(plateCor.toFixed(4)) : plateCor,
            headRawCor: Number.isFinite(headCor) ? Number(headCor.toFixed(4)) : headCor,
            plateAdjustment: Number.isFinite(plateAdjustment) ? Number(plateAdjustment.toFixed(4)) : plateAdjustment,
            adjustedHeadCor: Number.isFinite(adjustedHeadCor) ? Number(adjustedHeadCor.toFixed(4)) : adjustedHeadCor,
            differential: Number.isFinite(differential) ? Number(differential.toFixed(4)) : differential,
            faceDeformationDelta: Number.isFinite(faceDeformationDelta) ? String(Number(faceDeformationDelta.toFixed(4))) : '',
            faceDeformationStart: String(rawFaceDeformationStart ?? ''),
            faceDeformationEnd: String(rawFaceDeformationEnd ?? '')
        },
        meta: {
            researchQuadShots: [...plateResearchQuadShots, ...headResearchQuadShots],
            shotsOk,
            plateCountOk,
            headCountOk,
            massesOk,
            corReady,
            plateCaution,
            confirmationSignature,
            plateShotCount: plateShots.length,
            headShotCount: headShots.length,
            checkedPlateShotCount: checkedPlateResearchQuadShots.length,
            checkedHeadShotCount: checkedHeadResearchQuadShots.length,
            selectedShotCount: checkedPlateResearchQuadShots.length + checkedHeadResearchQuadShots.length,
            plateInvalidShots: plateResult?.invalidShots ?? 0,
            headInvalidShots: headResult?.invalidShots ?? 0
        }
    };
}

function ResearchQuadMasses({
    visible,
    selectedSetup = null,
    plateSetup = null,
    masses,
    onMassChange,
    onCorComputed,
    showPlaceholder = false,
    rows = [],
    disableInputs = false,
    persistOnChange = true,
    forceNotCalculated = false,
    confirmMassesOnly = false
}) {
    const { taskIdData } = useContext(TaskIdDataContext);
    const selectedSetupLocation = useMemo(
        () => (selectedSetup ? GetSetupLocation(selectedSetup) || selectedSetup.Id || 'Selected Setup' : 'No setup selected'),
        [selectedSetup]
    );
    const selectedSetupComponent = useMemo(
        () => (
            selectedSetup?.ClubId ||
            selectedSetup?.HeadSN ||
            selectedSetup?.BaseComponentSN ||
            ''
        ),
        [selectedSetup]
    );
    const corPayload = useMemo(
        () => buildResearchQuadCorPayload({ masses, rows, selectedSetup, plateSetup }),
        [masses, rows, selectedSetup, plateSetup]
    );

    const plateCor = corPayload.results.plateRawCor;
    const headCor = corPayload.results.headRawCor;
    const plateAdjustment = corPayload.results.plateAdjustment;
    const adjustedHeadCor = corPayload.results.adjustedHeadCor;
    const differential = corPayload.results.differential;
    const plateMass = corPayload.inputs.plateMass;
    const headMass = corPayload.inputs.headMass;
    const plateBallMass = corPayload.inputs.plateBallMass;
    const headBallMass = corPayload.inputs.headBallMass;
    const checkedPlateShots = corPayload.meta.plateShotCount;
    const checkedHeadShots = corPayload.meta.headShotCount;
    const shotsOk = corPayload.meta.shotsOk;
    const plateCountOk = corPayload.meta.plateCountOk;
    const plateCaution = corPayload.meta.plateCaution;
    const corReady = corPayload.meta.corReady;
    const massesOk = corPayload.meta.massesOk;
    const displayCorReady = !forceNotCalculated && corReady;
    const isPlateSetup = isPlateCorSetup(selectedSetup);
    const showMassSection = isPlateSetup || confirmMassesOnly;
    const showShotSection = !confirmMassesOnly;
    const showFaceDeformationSection = !confirmMassesOnly && !isPlateSetup;
    const showCalculatedCorSection = !confirmMassesOnly && !isPlateSetup;

    useEffect(() => {
        if (!onCorComputed || !persistOnChange) {
            return;
        }

        const hasAnyInput = [
            masses?.plate,
            masses?.plateBall,
            masses?.head,
            masses?.headBall,
            masses?.faceDeformationDelta,
            masses?.faceDeformationStart,
            masses?.faceDeformationEnd
        ].some((value) => (value ?? '').toString().trim() !== '');
        const hasAnyResult = plateCor !== null || headCor !== null || adjustedHeadCor !== null;

        if (!hasAnyInput && !hasAnyResult) {
            return;
        }

        onCorComputed({
            inputs: {
                plateMass,
                headMass,
                plateBallMass,
                headBallMass
            },
            results: {
                plateRawCor: plateCor,
                headRawCor: headCor,
                plateAdjustment,
                adjustedHeadCor,
                differential,
                faceDeformationDelta: corPayload.results.faceDeformationDelta,
                faceDeformationStart: corPayload.results.faceDeformationStart,
                faceDeformationEnd: corPayload.results.faceDeformationEnd
               
            },
            meta: {
                confirmationSignature: corPayload.meta.confirmationSignature,
                corReady: corPayload.meta.corReady
            }
        });
    }, [
        onCorComputed,
        persistOnChange,
        masses,
        plateMass,
        headMass,
        plateBallMass,
        headBallMass,
        plateCor,
        headCor,
        plateAdjustment,
        adjustedHeadCor,
        differential,
        corPayload
    ]);

    const handleChange = (field) => (event) => {
    if (!onMassChange) {
        return;
    }

    const isFaceDeformationField =
        field === 'faceDeformationStart' || field === 'faceDeformationEnd';

    if (disableInputs && !isFaceDeformationField) {
        return;
    }

    const nextValue = event.target.value;

    if (isFaceDeformationField && !isValidFaceDeformationInput(nextValue)) {
        return;
    }

    onMassChange({
        ...masses,
        [field]: nextValue
    });
};

    const getInputStyle = (field, { allowEmpty = false } = {}) => {
        const value = masses?.[field];
        const isEmpty = (value ?? '').toString().trim() === '';
        const isFaceDeformationField = [
            'faceDeformationStart',
            'faceDeformationDelta',
            'faceDeformationEnd',
            ].includes(field);
        const isValid = isFaceDeformationField
            ? isValidFaceDeformationInput(value)
            : (parseNumber(value) !== null || (allowEmpty && isEmpty));

        return {
            textAlign: 'right',
            backgroundColor: isValid ? '' : '#ffb3b3'
        };
    };

    return (
        <div className='research-quad-masses'>
            {/*<div className='research-quad-masses__title' style={{ textAlign: 'left', alignSelf: 'stretch' }}>COR:</div>*/}
            <div style={{ textAlign: 'left', marginBottom: '12px', fontSize: '22px' }}>
                <div>{selectedSetupLocation}</div>
                {!!selectedSetupComponent && (
                    <div style={{ fontSize: '18px', opacity: 0.8 }}>{selectedSetupComponent}</div>
                )}
            </div>

            {taskIdData && (
                <div style={{ textAlign: 'left', marginBottom: '12px' }}>
                    <div><strong>COR Hot Spot:</strong> {taskIdData.cor_use_hot_spot ? 'True' : 'False'}</div>
                    <div><strong>COR Center:</strong> {taskIdData.cor_use_center ? 'True' : 'False'}</div>
                </div>
            )}
            {visible && !selectedSetup && (
                <div className='research-quad-masses__message' style={{ marginBottom: '12px' }}>
                    Select a setup to view COR data.
                </div>
            )}
            {showMassSection && (
                <>
                    <br />
                    <div className='research-quad-masses__title' style={{ textAlign: 'left', alignSelf: 'stretch' }}>Masses:</div>
                    <div className='research-quad-masses__fields'>
                        {[
                            { key: 'plate', label: 'Plate' },
                            { key: 'plateBall', label: 'Plate Ball' },
                            { key: 'head', label: 'Head' },
                            { key: 'headBall', label: 'Head Ball' }
                        ].map((field) => (
                            <label key={field.key} className='research-quad-masses__field'>
                                <span className='research-quad-masses__label'>
                                    {field.label}:
                                </span>
                                <input
                                    type='number'
                                    step='0.1'
                                    value={masses?.[field.key] ?? ''}
                                    style={getInputStyle(field.key)}
                                    placeholder='Enter mass'
                                    onChange={handleChange(field.key)}
                                    readOnly={disableInputs}
                                />g
                            </label>
                        ))}
                    </div>
                </>
            )}
            {showShotSection && (
                <>
                    <br />
                    <div className='research-quad-masses__title' style={{ textAlign: 'left', alignSelf: 'stretch' }}>Shots:</div>
                    <div style={{ textAlign: 'left', fontSize: '22px' }}>
                        <div>Plate: {checkedPlateShots}/{PLATE_SHOT_COUNT}</div>
                        {!isPlateSetup && (
                            <div>Head: {checkedHeadShots}/{HEAD_SHOT_COUNT}</div>
                        )}
                        {shotsOk && (corPayload.meta.checkedPlateShotCount > PLATE_SHOT_COUNT || corPayload.meta.checkedHeadShotCount > HEAD_SHOT_COUNT) && (
                            <span>Using first {PLATE_SHOT_COUNT} plate shots and first {HEAD_SHOT_COUNT} head shots.</span>
                        )}
                    </div>
                </>
            )}
            {showFaceDeformationSection && (
                <>
                    <br />
                    <div className='research-quad-masses__title' style={{ textAlign: 'left', alignSelf: 'stretch' }}>Face Deformation:</div>
                    <div style={{ textAlign: 'left', fontSize: '22px' }}>
                        <label className='research-quad-masses__field'>
                            <span className='research-quad-masses__label'>
                                Start:
                            </span>
                            <input
                                type='text'
                                inputMode='decimal'
                                value={masses?.faceDeformationStart ?? ''}
                                style={{ ...getInputStyle('faceDeformationStart', { allowEmpty: true }), width: '80px' }}
                                onChange={handleChange('faceDeformationStart')}
                                //readOnly={disableInputs}
                            />
                        </label>
                        <label className='research-quad-masses__field'>
                            <span className='research-quad-masses__label'>
                                End:
                            </span>
                            <input
                                type='text'
                                inputMode='decimal'
                                value={masses?.faceDeformationEnd ?? ''}
                                style={{ ...getInputStyle('faceDeformationEnd', { allowEmpty: true }), width: '80px' }}
                                onChange={handleChange('faceDeformationEnd')}
                                //readOnly={disableInputs}
                            />
                        </label>
                    </div>
                </>
            )}
            {showCalculatedCorSection && (
                <>
                    <br />
                    <div
                        style={{
                            textAlign: 'left',
                            fontSize: '18px',
                            color: plateCountOk && massesOk ? '#22aa22' : '#cc3333'
                        }}
                    >
                        plate shots & masses: {plateCountOk && massesOk ? '✓' : 'X'}
                    </div>
                    <br />
                    <div className='research-quad-masses__title' style={{ textAlign: 'left', alignSelf: 'stretch' }}>Calculated COR:</div>

                    <div
                        style={{
                            background: 'red',
                            color: 'white',
                            padding: '8px',
                            textAlign: 'center',
                            borderRadius: '4px'
                        }}
                    >
                        {displayCorReady ? adjustedHeadCor.toFixed(4) : 'Not Calculated Yet'}
                    </div>
                    {displayCorReady && (
                        <div style={{ marginTop: '8px', fontSize: '14px', display: 'grid', gridTemplateColumns: '180px', columnGap: '12px', rowGap: '2px' }}>
                            <div>Plate USGA Raw:</div><div>{plateCor.toFixed(4)}</div>
                            <div>Head USGA Raw:</div><div>{headCor.toFixed(4)}</div>
                            <div>Differential (H-P):</div><div>{differential.toFixed(4)}</div>
                            {plateCaution && (
                                <div style={{ gridColumn: '1 / -1', color: '#faea0b' }}>
                                    Caution:
                                    {' '}
                                    Plate outside 0.840 +/- 0.004
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default ResearchQuadMasses;
